import _ from 'lodash';
import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { Card, CardBody, HeaderTitle, Table, MenuSVG } from '../../../components';
import { defaultDocument, defaultTelephone, defaultEmail, defaultLevels } from '../partials/defaults';
import { usersService, reportsService } from '../../../services';
import { FilterQueryParams } from '../../../helpers';
import { downloadListCSV, downloadCSV } from '../../../helpers/download';
import { useClientsUIContext } from './ClientsUIContext';
import { UIHelpers } from '../../../constants/pages/UIHelpers';
import clientsColumns from './ClientsColumns';
import DownloadModal from '../../movements/list/modal/DownloadModal';
import platforms from '../../../constants/users/platforms';
import { ROLES } from '../../../constants/permissions/roles';
import ClientsDeleteDialog from './ClientsDeleteDialog';
import { Box } from '@mui/material';
import mcStatuses from '../../../constants/makeandchecker/statuses';
import actionTypes from '../../../constants/makeandchecker/actionsTypeId';
import page from '../../../constants/pages/page';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { checkFileStatus } from '../../../helpers/fibonacciHelper';
import CustomAlert from '../../../components/Alert';

function ClientsTable ({ user, resetFilters, getSearch }) {
    // sortedKeyMap
    const customSortedKeyMap = { 'status.description': 'id_user_status' };
    const { sortedKeyMap, defaultSorted } = UIHelpers(customSortedKeyMap);
    const { t } = useTranslation();

    // States
    const [percents, setPercents] = useState(1);
    const [clients, setClients] = useState(0);
    const [openDownloadModal, setOpenDownloadModal] = useState(false);

    // Alert states
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: ''
    });
    const [openAlert, setOpenAlert] = useState(false);

    // Context state
    const clientsUIContext = useClientsUIContext();
    const clientsUIProps = useMemo(() => {
        return {
            isLoading: clientsUIContext.isLoading,
            setIsLoading: clientsUIContext.setIsLoading,
            queryParams: clientsUIContext.queryParams,
            setQueryParams: clientsUIContext.setQueryParams
        };
    }, [clientsUIContext]);

    // List state
    const [clientsList, setClientsList] = useState(null);

    // Dialogs state
    const [dialog, setDialog] = useState(null);

    // Roles state
    // eslint-disable-next-line
    const [userRoles, setUserRoles] = useState({
        admin: false,
        operator: false
    });

    const fetchUserRole = () => {
        // Check if logged in user is Admin
        const prepareRoles = {
            admin: false,
            operator: false
        };

        for (const userRole of user.roles) {
            if (userRole.role.id === ROLES.admin) {
                prepareRoles.admin = true;
            }
            if (userRole.role.id === ROLES.operator) {
                prepareRoles.operator = true;
            }
        }

        setUserRoles(prepareRoles);
        clientsUIProps.setIsLoading(true);
    };
        // Functions

    // Ids clients to drop
    const idDropList = [];

    const fetchClients = async (pageSize, page) => {
        // Filter for M&C
        const clientsDropList = [];

        const { sortField, sortOrder } = clientsUIProps.queryParams;

        // Prepare options (order, pages and filters)
        const filters = clientsUIProps.queryParams.filters || new FilterQueryParams();

        const options = {
            order: `${sortedKeyMap[sortField] || sortField}-${sortOrder.toUpperCase()}`,
            unionPeople: true,
            pageSize: pageSize || clientsUIProps.queryParams.pageSize,
            page: page || clientsUIProps.queryParams.pageNumber
        };

        // Filter only user for platform customer (Mobile applications (Android, IOS))
        filters.equal('idPlatform', platforms.consumer);

        // Set filters on options.
        options.filters = filters.build();
        try {
            const clients = await usersService.find(options);

            // change state clients list
            if (pageSize) {
                setPercents(page);
                downloadListCSV(clientsUIContext, clients, 'clients', page, setOpenDownloadModal);
            }
            else {
                setClientsList(clients.map(client => {
                    client.person = {
                        document: client?.person?.document || defaultDocument,
                        telephone: client.person?.telephone || defaultTelephone,
                        email: client.person?.email || defaultEmail,
                        levels: client.person?.levels[0] || defaultLevels,
                    };
                    client.person.telephone.description = client.person.telephone.description.replace('+549', '');
                    // M&C client
                    if (clientsDropList && _.includes(idDropList, client.id)) {
                        clientsDropList.forEach(c => {
                            if (client.id === c.metadata.path.id) {
                                client.makeandchecker = c;
                                client.mc_created_at = c.created_at;
                                client.makeandchecker.metadata.user = '';
                            }
                        });
                    }
                    return client;
                }));
            }

            return clients;
        }
        catch (e) {
            console.error('Error set client', e);
            throw e;
        }
    };

    const getClient = id => clientsList && clientsList.length ? _.find(clientsList, ['id', id]) : null;

    const getData = async () => {
        clientsUIProps.setIsLoading(true);

        try {
            await Promise.all([
                fetchClients(),
                quantity()
            ]);
            clientsUIProps.setIsLoading(false);
        }
        catch (error) {
            setAlert((prevAlert) => ({
                ...prevAlert,
                severity: 'error',
                title: t('errors.generalTitle'),
                description: t('errors.generalDescription'),
                className: 'clientsMC_Alert--error'
            }));
            setOpenAlert(true);

            clientsUIProps.setIsLoading(false);
        }
    };

    // Events

    // Handle query params changes. On any param change, fetch clients.
    useEffect(() => {
        if (JSON.stringify(clientsUIProps.queryParams.url) !== undefined) {
            getData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientsUIProps.queryParams]);
    // eslint-disable-next-line
    useEffect(() => fetchUserRole(), [clientsUIProps.queryParams]);
    useEffect(() => {
        clientsUIProps.setQueryParams(prev => ({ ...prev, pageSize: localStorage.getItem('sizePerPage') || page.pageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    , []);

    const handleClickClientDelete = id => {
        setDialog({
            type: 'delete',
            client: getClient(id)
        });
    };

    const handleClickMenuClient = id => {
        const client = getClient(id);
        const filterMc = new FilterQueryParams();

        filterMc.equal('idType', actionTypes.updateUserStatus);
        filterMc.equal('metadata.path.id', id);
        filterMc.equal('id_action_status', mcStatuses.pending.id);

        const option = {
            pageSize: 10000,
            filters: filterMc.build()
        };
        setDialog({
            type: 'menu',
            option,
            client,
        });
    };

    const quantity = async () => {
        const filters = clientsUIProps.queryParams.filters || new FilterQueryParams();
        filters.equal('idPlatform', platforms.consumer);
        const options = {
            unionPeople: true,
            pageSize: 1000000
        };
        options.filters = filters.build();
        await usersService
            .findSimple(options)
            .then(client => {
                setClients(client.length || 0);
            }
            );
    };

    const download = async () => {
        clientsUIProps.setIsLoading(true);

        const fetchFile = (idFile) => {
            return checkFileStatus({ filters: `id eq ${idFile}` }, 60, 3);
        };
        const startDate = clientsUIContext.queryParams?.filters?.filters?.created_at?.be[0] || '2023-08-21 06:00:00';
        const endDate = clientsUIContext.queryParams?.filters?.filters?.created_at?.be[1] || moment().utc().format('YYYY-MM-DD HH:mm:ss');
        const options = {
            idType: 25,
            startAt: startDate,
            endAt: endDate,
            metadata: {
                id_status: clientsUIContext.queryParams?.filters?.filters?.id_user_status?.eq || '',
                search: getSearch()
            }
        };
        const nameTable = window.location.pathname.slice(1, window.location.pathname.length);
        const fileName = startDate
            ? `Reporte del ${options.startAt} al ${moment().format('YYYY-MM-DD HH:mm:ss')} ${nameTable}.csv`
            : `Reporte ${nameTable}.csv`;
        await reportsService
            .createReport(options)
            .then(async (response) => {
                return await fetchFile(response.metadata.id_file);
            })
            .then(async (res) => {
                return await axios.get(res[0].description, { responseType: 'arraybuffer' });
            }
            )
            .then((response) => {
                clientsUIProps.setIsLoading(false);
                downloadCSV(response.data, fileName);
            })
            .catch(() => {
                clientsUIProps.setIsLoading(false);
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.downloadFile'),
                    className: 'clientsMC_Alert--error',
                }));
                setOpenAlert(true);
            });
    };

    // Main Component
    return (
        <>
            <DownloadModal
                show={openDownloadModal}
                percents={percents}
            />
            {(dialog && dialog.type === 'delete') && (
                <ClientsDeleteDialog
                    open={true}
                    client={dialog.client}
                    onClose={() => {
                        setDialog(null);
                    }}
                    onDelete={() => { }}
                    setAlert={setAlert}
                    setOpenAlert={setOpenAlert}
                />
            )}
            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />
            <Card>
                <Box >
                    <HeaderTitle
                        title={t('clients.table.title')}
                        total={clients}
                        reset={resetFilters}
                        icon={(<MenuSVG className='mr-1 mb-2' />)}
                        download={download}
                        UIProps={clientsUIProps}
                    />
                </Box>
                <CardBody>
                    <Table
                        data={clientsList}
                        columns={clientsColumns({ handleClickMenuClient, handleClickClientDelete, dialog, setDialog })}
                        sort={defaultSorted}
                        onChange={clientsUIProps.setQueryParams}
                        isLoading={clientsUIProps.isLoading}
                        total={clients}
                        pagination={{
                            sizePerPage: clientsUIProps.queryParams.pageSize,
                            page: clientsUIProps.queryParams.pageNumber
                        }}
                    />
                </CardBody>
            </Card>
        </>
    );
}

const mapStateToProps = ({ auth: { user } }) => ({
    user
});

export default connect(mapStateToProps)(ClientsTable);
